<script setup lang="ts">
import type { SettingsSection } from '#layers/global-settings/constants/settings-overlay-panels';
import type { BadgeProps, SidebarItem } from '#layers/ui/public';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from '#layers/access/public';
import { extensionsFlag, SystemVersion } from '#layers/base/public';
import { useSettingsOverlay } from '#layers/global-settings/composables/use-settings-overlay';
import { DOCS_URL, FEEDBACK_URL, RELEASE_NOTES_URL, ROADMAP_URL } from '#layers/global-settings/constants/external-links';

const { t } = useI18n();
const { activeSection, navigate } = useSettingsOverlay();

const { isOrganizationAdmin } = storeToRefs(useAuth());

function item(section: SettingsSection, icon: string, label: string, badge?: BadgeProps) {
	// These items navigate via `onSelect` (no `to`), so DSidebar renders them as a plain <span>
	// without a pointer cursor — add it back. The section headers below are `hideCollapse`, so
	// they're non-interactive and should keep the default cursor.
	return { id: section, icon, label, badge, active: activeSection.value === section, onSelect: () => navigate(section), ui: { wrapper: 'cursor-pointer' } };
}

function externalItem(id: string, icon: string, label: string, url: string): SidebarItem {
	return { id, icon, label, to: url, target: '_blank' };
}

function group(id: string, label: string, children: SidebarItem[]) {
	return [{ id, label, section: true, hideCollapse: true, class: 'cursor-default', children }];
}

const items = computed(() => [
	group('account', t('overlay.groups.account'), [
		item('account/profile', 'lucide:user', t('account.settings.profile.title')),
		item('account/access', 'lucide:shield-user', t('account.settings.access.title')),
	]),
	group('organization', t('overlay.groups.organization'), [
		item('organization/general', 'lucide:settings', t('organization.dashboard.settings.organization.general.title')),
		item('organization/workspaces', 'lucide:layout-grid', t('organization.dashboard.workspaces.title')),
		...(extensionsFlag.value ? [item('organization/extensions', 'lucide:blocks', t('extensions.title'), { color: 'warning', variant: 'subtle', label: t('common.experimental') })] : []),
		item('organization/members', 'lucide:users', t('organization.dashboard.members.title')),
		item('organization/audit-logs', 'lucide:logs', t('organization.dashboard.settings.access.audit_logs.title')),
		...(isOrganizationAdmin.value ? [item('organization/license', 'lucide:gem', t('organization.dashboard.settings.license.title'))] : []),
	]),
	group('access', t('overlay.groups.access'), [
		item('organization/api-keys', 'lucide:key-round', t('organization.dashboard.settings.access.api_keys.title')),
		item('organization/service-accounts', 'lucide:user-cog', t('organization.dashboard.settings.access.service_accounts.title')),
		item('organization/sso', 'lucide:shield-check', t('organization.dashboard.settings.access.providers.title')),
	]),
	group('help', t('overlay.groups.help'), [
		externalItem('help/docs', 'lucide:book-open', t('overlay.help.docs'), DOCS_URL),
		externalItem('help/release-notes', 'lucide:rocket', t('overlay.help.release_notes'), RELEASE_NOTES_URL),
		externalItem('help/roadmap', 'lucide:map', t('overlay.help.roadmap'), ROADMAP_URL),
		externalItem('help/feedback', 'lucide:message-square-text', t('overlay.help.feedback'), FEEDBACK_URL),
	]),
]);
</script>

<template>
	<DSidebar :items>
		<template #content-top>
			<SettingsOverlayHeader class="px-2 pt-1 pb-3" />
		</template>
		<template #footer>
			<SystemVersion />
		</template>
	</DSidebar>
</template>
